import React, { useEffect, useState, useRef, useCallback } from "react";

// Modules
import moment from "moment";
import { debounce } from "lodash";
import { NestedMenuItem } from "mui-nested-menu";

// App
import { serverUrl, filesystem_root, subdomain } from "../../config";
import { patchNode } from "../../core/postNode";
import { decodeHtmlEntities, humanFileSize } from "../../utils/helpers";

// UI components
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  Checkbox,
  Slider,
  Alert,
  FormControl,
  Button,
  Stack,
  InputLabel,
  Typography,
  Select,
  OutlinedInput,
  MenuItem,
} from "@mui/material/";
import DoneIcon from "@mui/icons-material/Done";
import { ChevronLeft } from "@mui/icons-material";
import BackButton from "./backButton";

const input_font_size = 14;

const menuProps = {
  anchorOrigin: {
    vertical: "top",
    horizontal: "left",
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "right",
  },
  PaperProps: { style: { pointerEvents: "auto" } },
};

export default function SelectedVideo(props) {
  const [isError, setError] = useState(null);
  const [sliderValue, setSliderValue] = useState(
    props.selectedMedia.field_poster_timestamp
      ? props.selectedMedia.field_poster_timestamp
      : 0
  );
  const [duration, setDuration] = useState(0);
  const [isPatching, setPatching] = useState(false);
  const video = useRef(null);
  const [poster, setPoster] = useState(
    `${filesystem_root}/${subdomain}/videos/${
      props.selectedMedia.mid
    }.jpg?t=${Date.now()}`
  );

  // core
  const [coreLibrary, setCoreLibrary] = useState(
    props.selectedMedia.core_library === "1" ? true : false
  );

  // category
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedCategoryItem, setSelectedCategoryItem] = useState(
    props.selectedMedia
      ? {
          value: props.selectedMedia?.category?.tid
            ? props.selectedMedia?.category?.tid
            : 0,
        }
      : { value: 0 }
  );

  // category select
  const [open, setOpen] = useState(false);

  const formatDuration = (value) => {
    const minute = Math.floor(value / 60);
    const secondLeft = value - minute * 60;
    return `${minute}:${secondLeft < 10 ? `0${secondLeft}` : secondLeft}`;
  };

  const handleLoadedMetadata = () => {
    if (video) {
      setDuration(video.current.duration);
      // createPoster(video.current, props.selectedMedia.field_media_video, sliderValue);
    }
  };

  const handleTimestamp = (value) => {
    setSliderValue(value);
    debounceUpdate(value);
  };

  const handleCoreLibrary = (checked) => {
    setPatching(true);
    setCoreLibrary(checked ? true : false);
    syncData(selectedCategory, sliderValue, checked);
  };

  const handleCategorySelection = (tid) => {
    setOpen(false);

    let catItem = props.categories.filter((item) => {
      return item.value === tid;
    });

    setSelectedCategoryItem({
      label: catItem[0].label,
      value: catItem[0].value,
    });

    setPatching(true);
    setSelectedCategory(parseInt(catItem[0].value, 10));
    syncData(catItem[0].value, sliderValue, coreLibrary);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceUpdate = useCallback(
    debounce(
      (value) => {
        setPatching(true);
        syncData(selectedCategory, value, coreLibrary);
      },
      1500,
      true
    ),
    []
  );

  /** Updates video poster timestamp */
  const syncData = (category, timestamp, core_library) => {
    const data = {
      mid: props.selectedMedia.mid,
      category: category,
      field_poster_timestamp: timestamp,
      core_library: core_library,
    };

    patchNode(
      `media_library/${props.selectedMedia.mid}`,
      data,
      props.user.csrf_token
    )
      .then((_response) => {
        setPatching(false);
        // props.onTimestampUpdate();
        props.refreshContent();
      })
      .catch((error) => {
        setPatching(false);
      });
  };

  // Poster refresh
  useEffect(() => {
    if (props.selectedMedia.field_poster_timestamp === sliderValue) {
      return;
    }

    const timeout = setTimeout(() => {
      const image = new Image();

      image.onload = () => {
        const url = `${filesystem_root}/${subdomain}/videos/${
          props.selectedMedia.mid
        }.jpg?t=${Date.now()}`;

        video.current.plyr.poster = url;
        setPoster(url);
      };

      image.onerror = () => {
        setPoster(null);
      };

      image.src = `${filesystem_root}/${subdomain}/videos/${
        props.selectedMedia.mid
      }.jpg?t=${Date.now()}`;
    }, 5000);

    return () => clearTimeout(timeout);
  }, [props.selectedMedia.field_poster_timestamp, props.selectedMedia.mid, sliderValue]);

  // MutationObserver to track new video elements
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const videos = document.querySelectorAll(".plyr:not([data-initialized])");

      videos.forEach((video) => {
        new window.Plyr(video, {
          controls: [
            "play-large",
            "play",
            "progress",
            "current-time",
            "mute",
            "volume",
            "fullscreen",
          ],
        });
        video.setAttribute("data-initialized", "true"); // Prevent re-initialization
      });
    });

    // Observe changes in the body for dynamically added elements
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect(); // Cleanup observer on unmount
  }, []);

  const allCategoryValues = React.useMemo(() => {
    // only include real taxonomy IDs, skip 0 because "All Categories" is added manually
    return props.categories.map((c) => c.value);
  }, [props.categories]);

  const renderMenuItems = (parentId = null) => {
    const items = props.categories.filter(
      (item) => item.parent_target_id === parentId
    );

    return items.map((item) => {
      const children = props.categories.filter(
        (child) => child.parent_target_id === item.value
      );

      if (children.length > 0) {
        return (
          <NestedMenuItem
            key={`cat-${item.value}`}
            leftIcon={<ChevronLeft />}
            rightIcon={null}
            parentMenuOpen={open}
            label={decodeHtmlEntities(item.label)}
            MenuProps={menuProps}
            className="media-category-nested"
            onClick={() => handleCategorySelection(item.value)}
          >
            {renderMenuItems(item.value)}
          </NestedMenuItem>
        );
      }

      return (
        <MenuItem
          key={`cat-${item.value}`}
          value={item.value}
          sx={{ fontSize: input_font_size }}
          onClick={() => handleCategorySelection(item.value)}
        >
          {decodeHtmlEntities(item.label)}
        </MenuItem>
      );
    });
  };

  return (
    <>
      {isError ? (
        <>
          <Row className="mt-3 mb-3 justify-content-center">
            <Col>
              <div
                className={`crop-actions ${
                  props.isNotForInsert ? "singular" : ""
                }`}
              >
                <BackButton onCancel={props.onCancel} />
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <Alert
                variant="filled"
                severity="error"
                onClose={() => {
                  setError(null);
                }}
              >
                <p>{isError}</p>
              </Alert>
            </Col>
          </Row>
        </>
      ) : (
        <>
          <Row className="mt-3 mb-3 justify-content-center">
            <Col>
              <div
                className={`crop-actions ${
                  props.isNotForInsert ? "singular" : ""
                }`}
              >
                <BackButton onCancel={props.onCancel} />
                {!props.isNotForInsert && (
                  <Stack direction="row" spacing={2} textAlign="center">
                    <Button
                      variant="contained"
                      color="secondary"
                      endIcon={<DoneIcon />}
                      onClick={() => {
                        props.onMediaInsert({
                          ...props.selectedMedia,
                          field_poster_timestamp: sliderValue,
                        });
                      }}
                    >
                      Select
                    </Button>
                  </Stack>
                )}
              </div>
            </Col>
          </Row>

          <Row className="mt-3 mb-3 justify-content-center">
            <Col>
              <div className="text-center">
                <video
                  ref={video}
                  className="plyr video-item"
                  src={`${serverUrl}/api/v2/assets?mid=${props.selectedMedia.mid}`}
                  controls={true}
                  data-mid={props.selectedMedia.mid}
                  data-poster={poster}
                  poster={poster}
                  playsInline={true}
                  onLoadedMetadata={handleLoadedMetadata}
                />
              </div>
            </Col>
            <Col sm={12} md={6} className={"mb-3 info-container"}>
              <div className="wrapper">
                <div className="form-group">
                  <Row className="mb-2 align-items-center info-row">
                    <Col xs={4} sm={3}>
                      <Typography variant="form-group-label" owner="admin">
                        File name
                      </Typography>
                    </Col>
                    <Col>
                      <Typography
                        variant="form-group-label"
                        owner="admin"
                        weight="regular"
                        color="adminText.main"
                      >
                        {props.selectedMedia.name}
                      </Typography>
                    </Col>
                  </Row>
                  <Row className="mb-2 align-items-center info-row">
                    <Col xs={4} sm={3}>
                      <Typography variant="form-group-label" owner="admin">
                        MID
                      </Typography>
                    </Col>
                    <Col>
                      <Typography
                        variant="form-group-label"
                        owner="admin"
                        weight="regular"
                        color="adminText.main"
                      >
                        {props.selectedMedia.mid}
                      </Typography>
                    </Col>
                  </Row>
                  <Row className="mb-2 align-items-center info-row">
                    <Col xs={4} sm={3}>
                      <Typography variant="form-group-label" owner="admin">
                        Created
                      </Typography>
                    </Col>
                    <Col>
                      <Typography
                        variant="form-group-label"
                        owner="admin"
                        weight="regular"
                        color="adminText.main"
                      >
                        {moment
                          .unix(props.selectedMedia.created)
                          .format("h:mm:ssa - DD/MM/YYYY")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row className="mb-2 align-items-center info-row">
                    <Col xs={4} sm={3}>
                      <Typography variant="form-group-label" owner="admin">
                        Author
                      </Typography>
                    </Col>
                    <Col>
                      <Typography
                        variant="form-group-label"
                        owner="admin"
                        weight="regular"
                        color="adminText.main"
                        break="break-all"
                      >
                        {props.selectedMedia.username}
                      </Typography>
                    </Col>
                  </Row>
                  <Row className="mb-2 align-items-center info-row">
                    <Col xs={4} sm={3}>
                      <Typography variant="form-group-label" owner="admin">
                        Thumbnail timestamp
                      </Typography>
                    </Col>
                    <Col>
                      <Typography
                        variant="form-group-label"
                        owner="admin"
                        weight="regular"
                        color="adminText.main"
                      >
                        {formatDuration(sliderValue)}
                      </Typography>
                    </Col>
                  </Row>
                  <Row className="mb-2 align-items-center info-row">
                    <Col>
                      <Slider
                        sx={{ width: "95%", left: "2%" }}
                        max={duration}
                        min={0}
                        value={sliderValue}
                        onChange={(event, value) => {
                          handleTimestamp(value);
                        }}
                        steps={1}
                        disabled={isPatching ? true : false}
                      />
                    </Col>
                  </Row>
                  {props.selectedMedia.filesize && (
                    <Row className="mb-2 align-items-center info-row">
                      <Col xs={4} sm={3}>
                        <Typography variant="form-group-label" owner="admin">
                          Filesize
                        </Typography>
                      </Col>
                      <Col>
                        <Typography
                          variant="form-group-label"
                          owner="admin"
                          weight="regular"
                          color="adminText.main"
                          break="break-all"
                        >
                          {humanFileSize(props.selectedMedia.filesize)}
                        </Typography>
                      </Col>
                    </Row>
                  )}
                  <Row className="mb-2 align-items-center info-row">
                    <Col xs={4} sm={3}>
                      <Typography variant="form-group-label" owner="admin">
                        Category
                      </Typography>
                    </Col>
                    <Col>
                      <FormControl
                        className="info-category-select"
                        size="small"
                        name="mediaCategory"
                        variant="outlined"
                        sx={{
                          width: 1,
                        }}
                      >
                        <InputLabel
                          sx={{
                            fontSize: input_font_size,
                            color: "primary.main",
                          }}
                          id="categories"
                        >
                          Category
                        </InputLabel>
                        <Select
                          size="small"
                          labelId="categories"
                          id="categories-standard"
                          defaultValue={props.categories[0]?.value}
                          value={selectedCategoryItem?.value}
                          label="Category"
                          open={open}
                          onClose={() => setOpen(false)}
                          onOpen={() => setOpen(true)}
                          renderValue={(selected) => {
                            const stringified = String(selected);

                            if (!stringified || stringified === "0")
                              return "All Categories";
                            const found = props.categories.find(
                              (c) => c.value === stringified
                            );

                            return found
                              ? decodeHtmlEntities(found.label)
                              : "All Categories";
                          }}
                          sx={{ fontSize: input_font_size }}
                          input={
                            <OutlinedInput
                              sx={{
                                fontSize: input_font_size,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#c2ccd6",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderWidth: "2px",
                                  borderColor: "primary.main",
                                },
                              }}
                              label="Category"
                            />
                          }
                        >
                          {allCategoryValues.map((id) => (
                            <MenuItem
                              key={`register-${id}`}
                              value={id}
                              sx={{ display: "none" }}
                            />
                          ))}

                          {/* Visible top-level All Categories */}
                          <MenuItem
                            key="all-categories"
                            value={0}
                            sx={{ fontSize: input_font_size }}
                            onClick={() => handleCategorySelection(0)}
                          >
                            All Categories
                          </MenuItem>

                          {renderMenuItems(null)}
                        </Select>
                      </FormControl>
                    </Col>
                  </Row>
                  {props.permissions.access_media_promote_to_library && (
                    <Row className="mb-2 align-items-center info-row">
                      <Col xs={4} sm={3}>
                        <Typography variant="form-group-label" owner="admin">
                          Corporate library
                        </Typography>
                      </Col>
                      <Col>
                        <Checkbox
                          sx={{
                            marginLeft: "-13px",
                          }}
                          size="small"
                          id="core-lib-checkbox"
                          disabled={isPatching}
                          checked={coreLibrary}
                          onChange={(event) => {
                            handleCoreLibrary(
                              event.target.checked ? true : false
                            );
                          }}
                        />
                      </Col>
                    </Row>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </>
      )}
    </>
  );
}
